import I18n from 'common/i18n';
import { ViewColumn } from 'common/types/viewColumn';
import { useCallback } from 'react';
import { ExportData } from '../types';
import { AgGridReact } from '@ag-grid-community/react';
import moment from 'moment';
import { hideToastById } from 'common/components/ToastNotification/Toastmaster';
import { NULL_GROUPING } from '../Constants';
import { processCellCallback } from '../helpers/processCellCallback';
import { isSubtotalCell } from '../helpers/isTotalCell';

export interface IInitializeExportCallback {
  selectedFiltered: boolean;
}

interface IUseInitializeExport {
  viewColumns: ViewColumn[];
  getExportData: ((selectedFiltered: boolean) => Promise<ExportData>) | undefined;
  setIsExportTableRendered: React.Dispatch<React.SetStateAction<boolean>>;
  setRowsToBeExported: React.Dispatch<React.SetStateAction<unknown[] | undefined>>;
}

/**
 *
 * @param viewColumns Visible columns in the rendered AgGrid table
 * @param getExportData A method that fetches the data that is needed to be exported
 * @param setIsExportTableRendered A `setState` method that handles the condition for determining if the export table has rendered
 * @param setRowsToBeExported A `setState` method that handles the rows of the rendered export table
 * @returns The method to initialize the export table
 */
export const useInitializeExport = ({
  viewColumns,
  getExportData,
  setIsExportTableRendered,
  setRowsToBeExported
}: IUseInitializeExport) => {
  return useCallback(
    (data: IInitializeExportCallback) => {
      if (viewColumns.length > 0 && getExportData) {
        getExportData(data.selectedFiltered)?.then((exportData) => {
          setIsExportTableRendered(true);
          setRowsToBeExported(exportData.rows);
        });
      }
    },
    [viewColumns, getExportData, setIsExportTableRendered, setRowsToBeExported]
  );
};

interface IUseHandleExport {
  gridRef: React.RefObject<AgGridReact<unknown>>;
  isExportTableRendered: boolean;
  rowsToBeExported: unknown[] | undefined;
  datasetName: string | undefined;
  setIsExportTableRendered: React.Dispatch<React.SetStateAction<boolean>>;
  setRowsToBeExported: React.Dispatch<React.SetStateAction<unknown[] | undefined>>;
}

/**
 *
 * @param gridRef The reference object to the rendered export table
 * @param datasetName The name of the dataset to be set as part of the filename of the exported XLSX file
 * @param isExportTableRendered The condition for determining if the export table has rendered
 * @param setIsExportTableRendered A `setState` method that handles the condition for determining if the export table has rendered
 * @param rowsToBeExported The rows of the rendered export table
 * @param setRowsToBeExported A `setState` method that handles the rows of the rendered export table
 * @param vifOrderConfig Contains the sorting configuration of the table
 * @returns The actual method that exports the rendered table
 */
export const useHandleExport = ({
  gridRef,
  datasetName,
  isExportTableRendered,
  rowsToBeExported,
  setIsExportTableRendered,
  setRowsToBeExported
}: IUseHandleExport) => {
  return useCallback(() => {
    if (isExportTableRendered && gridRef.current && rowsToBeExported !== undefined) {
      const exportGridApi = gridRef.current.api;
      const currentDate = moment().format('YYYYMMDD');
      const fileName = `${datasetName}_${currentDate}`;

      exportGridApi.exportDataAsExcel({
        fileName: fileName,
        processCellCallback,
        processRowGroupCallback(params) {
          const { node } = params;
          const noValueText = I18n.t('shared.visualizations.charts.common.summary_table.no_value');
          const value = node.key === NULL_GROUPING ? noValueText : node.key;
          // Grouping value
          if (!node.footer || isSubtotalCell(params)) {
            return value;
          }
          return '';
        }
      });
      hideToastById('export-modal-toast');
      setIsExportTableRendered(false);
      setRowsToBeExported(undefined);
    }
  }, [
    datasetName,
    gridRef,
    isExportTableRendered,
    rowsToBeExported,
    setIsExportTableRendered,
    setRowsToBeExported
  ]);
};
